import React, { createContext, useContext, useEffect, useReducer } from 'react';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import type {
  ChatWithDocumentResponse,
  ExtractionResultItem,
  RagSourceReference,
} from '../../../../api/document-rag/document-rag.api.types';
import { SUBMISSION_SOURCE } from '../../../../api/submissions/submissions.api.types';
import { FEATURE_FLAG_KEYS } from '../../../../config/growthbook';
import { useGetChatHistory } from '../_hooks/useGetChatHistory';
import {
  getConversationId,
  setConversationId,
} from '../_hooks/utils/document-rag-conversation.storage';

/**
 * Chat message interface matching DocumentChatModal's ChatMessage
 */
export interface ChatMessage {
  question: string;
  response: ChatWithDocumentResponse;
  timestamp: Date;
  /** Optional extraction data from chat history (new format with detailed extraction data) - can be single or array for multiple values */
  extractionData?: ExtractionResultItem | ExtractionResultItem[];
  /** Whether this message is in a loading state (temporary message waiting for response) */
  isLoading?: boolean;
}

/**
 * State managed by DocumentRagContext
 */
export interface DocumentRagState {
  /** Conversation ID for maintaining context across messages */
  conversationId: string | null;
  /** Array of chat messages (question + response + timestamp) */
  messages: ChatMessage[];
  /** Currently selected document ID for preview */
  selectedDocumentId: string | null;
  /** Submission ID (required for conversation ID persistence) */
  submissionId: string | null;
  /** Message ID associated with the submission */
  messageId: string | null;
  /** Submission source */
  submissionSource: SUBMISSION_SOURCE | null;
  /** Whether the chat modal is open */
  isChatOpen: boolean;
  /** Citation that was clicked to open the modal (for initial page navigation) */
  clickedCitation: RagSourceReference | null;
}

/**
 * Actions for DocumentRagContext reducer
 */
export type DocumentRagAction =
  | { type: 'SET_CONVERSATION_ID'; payload: string | null }
  | { type: 'ADD_MESSAGE'; payload: ChatMessage }
  | { type: 'UPDATE_MESSAGE'; payload: { index: number; message: ChatMessage } }
  | { type: 'SET_MESSAGES'; payload: ChatMessage[] }
  | { type: 'CLEAR_MESSAGES' }
  | { type: 'SET_SELECTED_DOCUMENT_ID'; payload: string | null }
  | {
      type: 'SET_SUBMISSION_CONTEXT';
      payload: {
        submissionId: string | null;
        messageId: string | null;
        submissionSource: SUBMISSION_SOURCE | null;
      };
    }
  | {
      type: 'SET_IS_CHAT_OPEN';
      payload: boolean;
    }
  | {
      type: 'SET_CLICKED_CITATION';
      payload: RagSourceReference | null;
    }
  | {
      type: 'RESET';
      payload: {
        submissionId: string | null;
        messageId: string | null;
        submissionSource: SUBMISSION_SOURCE | null;
      };
    };

/**
 * Initial state for DocumentRagContext
 */
const initialState: DocumentRagState = {
  conversationId: null,
  messages: [],
  selectedDocumentId: null,
  submissionId: null,
  messageId: null,
  submissionSource: null,
  isChatOpen: false,
  clickedCitation: null,
};

/**
 * Reducer function for DocumentRagContext state management
 */
function documentRagReducer(
  state: DocumentRagState,
  action: DocumentRagAction
): DocumentRagState {
  switch (action.type) {
    case 'SET_IS_CHAT_OPEN':
      return {
        ...state,
        isChatOpen: action.payload,
      };

    case 'SET_CONVERSATION_ID':
      return {
        ...state,
        conversationId: action.payload,
      };

    case 'ADD_MESSAGE':
      return {
        ...state,
        messages: [...state.messages, action.payload],
      };

    case 'UPDATE_MESSAGE': {
      const updatedMessages = [...state.messages];
      if (
        action.payload.index >= 0 &&
        action.payload.index < updatedMessages.length
      ) {
        updatedMessages[action.payload.index] = action.payload.message;
      }

      return {
        ...state,
        messages: updatedMessages,
      };
    }

    case 'SET_MESSAGES':
      return {
        ...state,
        messages: action.payload,
      };

    case 'CLEAR_MESSAGES':
      return {
        ...state,
        messages: [],
      };

    case 'SET_SELECTED_DOCUMENT_ID':
      return {
        ...state,
        selectedDocumentId: action.payload,
      };

    case 'SET_CLICKED_CITATION':
      return {
        ...state,
        clickedCitation: action.payload,
      };

    case 'SET_SUBMISSION_CONTEXT':
      return {
        ...state,
        submissionId: action.payload.submissionId,
        messageId: action.payload.messageId,
        submissionSource: action.payload.submissionSource,
      };

    case 'RESET':
      // Reset state for new submission - clear messages
      // Conversation ID will be loaded in useEffect
      return {
        ...state,
        submissionId: action.payload.submissionId,
        messageId: action.payload.messageId,
        submissionSource: action.payload.submissionSource,
        messages: [],
        conversationId: null,
        selectedDocumentId: null,
        clickedCitation: null,
      };

    default:
      return state;
  }
}

/**
 * Context value type
 */
interface DocumentRagContextValue {
  /** Current state */
  state: DocumentRagState;
  /** Dispatch function for actions */
  dispatch: React.Dispatch<DocumentRagAction>;
}

const DocumentRagContext = createContext<DocumentRagContextValue | null>(null);

/**
 * Props for DocumentRagProvider
 */
export interface DocumentRagProviderProps {
  /** Submission ID (required for conversation ID persistence) */
  submissionId: string;
  /** Message ID associated with the submission */
  messageId: string | null;
  /** Submission source */
  submissionSource: SUBMISSION_SOURCE;
  /** Child components */
  children: React.ReactNode;
}

/**
 * Provider component for DocumentRagContext
 * Manages document RAG state including conversation ID, message history, and selected document
 */
export function DocumentRagProvider({
  submissionId,
  messageId,
  submissionSource,
  children,
}: DocumentRagProviderProps) {
  const [state, dispatch] = useReducer(documentRagReducer, initialState);
  const isDocRagEnabled = useFeatureIsOn(FEATURE_FLAG_KEYS.SUBMISSIONS_DOC_RAG);

  // Load chat history on submission load - use as initial data only
  const {
    conversationId: historyConversationId,
    isLoading: isHistoryLoading,
    isError: isHistoryError,
  } = useGetChatHistory({
    submissionId,
    enabled: Boolean(submissionId) && isDocRagEnabled,
  });

  // Initialize submission context and conversation ID on mount
  // Reset state when submissionId or messageId changes (new submission = new conversation)
  useEffect(() => {
    dispatch({
      type: 'RESET',
      payload: { submissionId, messageId, submissionSource },
    });

    // Load conversation ID from localStorage after reset (fallback)
    const storedConversationId = getConversationId(submissionId);
    if (storedConversationId) {
      dispatch({
        type: 'SET_CONVERSATION_ID',
        payload: storedConversationId,
      });
    }
  }, [submissionId, messageId, submissionSource]);

  // Update conversationId from chat history API when available
  // Fallback strategy: history API -> localStorage -> null
  useEffect(() => {
    if (!isHistoryLoading && submissionId) {
      if (historyConversationId) {
        // Use conversationId from history API (preferred)
        dispatch({
          type: 'SET_CONVERSATION_ID',
          payload: historyConversationId,
        });
      } else if (isHistoryError) {
        // History API failed - fall back to localStorage
        const storedConversationId = getConversationId(submissionId);
        if (storedConversationId) {
          dispatch({
            type: 'SET_CONVERSATION_ID',
            payload: storedConversationId,
          });
        }
        // If both fail, conversationId remains null (will be set by backend on first extraction)
      }
      // If history API returns null conversationId, proceed without it
    }
  }, [historyConversationId, isHistoryLoading, isHistoryError, submissionId]);

  // Persist conversation ID to localStorage when it changes
  useEffect(() => {
    if (state.conversationId && state.submissionId) {
      try {
        setConversationId(state.submissionId, state.conversationId);
      } catch (error) {
        console.error(
          `Error persisting conversation ID to localStorage for submission ${state.submissionId}:`,
          error
        );
      }
    }
  }, [state.conversationId, state.submissionId]);

  const contextValue: DocumentRagContextValue = {
    state,
    dispatch,
  };

  return (
    <DocumentRagContext.Provider value={contextValue}>
      {children}
    </DocumentRagContext.Provider>
  );
}

/**
 * Hook to access DocumentRagContext
 * @throws Error if used outside DocumentRagProvider
 */
export function useDocumentRagContext() {
  const context = useContext(DocumentRagContext);
  if (!context) {
    throw new Error(
      'useDocumentRagContext must be used within DocumentRagProvider'
    );
  }
  return context;
}

/**
 * Hook to safely access DocumentRagContext without throwing
 * Returns null if used outside DocumentRagProvider
 */
export function useDocumentRagContextSafe() {
  return useContext(DocumentRagContext);
}
